import React, { useEffect, useState } from "react";
import {
  Database,
  Gear,
  Shop,
  Phone,
  BarChart,
  Envelope,
  Files
} from "react-bootstrap-icons";
import { Link } from "react-router-dom";

export default function Pricing() {
  useEffect(() => {
    window.scrollTo(0, 0);
  });

  const BASE_PRICE = 19.5;
  const EXTRA_PAGE_PRICE = 2;

  const [selectedAddOns, setSelectedAddOns] = useState({
    ecommerce: false,
    database: false,
    custom: false,
    pwa: false,
    analytics: false,
    contactForm: false
  });

  const [extraPages, setExtraPages] = useState(0);

  const toggleAddOn = (key) => {
    setSelectedAddOns((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const addPage = () => setExtraPages((n) => n + 1);
  const removePage = () => setExtraPages((n) => Math.max(0, n - 1));

  const totalPrice =
    BASE_PRICE +
    (selectedAddOns.ecommerce ? 15 : 0) +
    (selectedAddOns.database ? 12 : 0) +
    (selectedAddOns.custom ? 8 : 0) +
    (selectedAddOns.pwa ? 4 : 0) +
    (selectedAddOns.analytics ? 3 : 0) +
    (selectedAddOns.contactForm ? 4 : 0) +
    extraPages * EXTRA_PAGE_PRICE;

  const hasAddOnsSelected =
    Object.values(selectedAddOns).some((v) => v === true) || extraPages > 0;

  return (
    <div className="container mt-5">
      <h1 className="fw-bold text-center mb-4">Pricing</h1>

      <p className="text-center text-muted mb-4">
        A one page website for your business starts at £19.50 per month. Choose
        optional add ons below to see an estimated monthly price.
      </p>

      <div className="row justify-content-center mb-5">
        <div className="col-md-6">
          <div className="p-4 rounded-4 shadow-sm text-center custom-card-light">
            <p className="text-uppercase small text-muted mb-1">
              Your estimated monthly price
            </p>
            <h2 className="fw-bold mb-0">£{totalPrice.toFixed(2)}</h2>
            <p className="text-muted mt-3 mb-0">
              Base price is £19.50 per month
              {hasAddOnsSelected
                ? " plus the add ons you have selected."
                : ". Click an add on or add extra pages to update the price."}
            </p>
          </div>
        </div>
      </div>

      <h2 className="fw-bold text-center mb-4">Add Ons</h2>

      <div className="row g-4">
        <div className="col-md-4">
          <button
            onClick={() => toggleAddOn("contactForm")}
            className="w-100 btn p-0 border-0 bg-transparent"
          >
            <div
              className={
                "p-4 rounded-4 shadow-sm text-center custom-card" +
                (selectedAddOns.contactForm ? " border border-primary" : "")
              }
            >
              <div className="icon-circle">
                <Envelope />
              </div>
              <h5 className="fw-bold">Contact form</h5>
              <p className="text-muted">
                Visitors can message you without showing your email.
              </p>
              <p className="fw-bold mb-0">From £4 per month</p>
              {selectedAddOns.contactForm && (
                <p className="small text-primary mt-2 mb-0">
                  Included in estimate
                </p>
              )}
            </div>
          </button>
        </div>

        <div className="col-md-4">
          <button
            onClick={() => toggleAddOn("analytics")}
            className="w-100 btn p-0 border-0 bg-transparent"
          >
            <div
              className={
                "p-4 rounded-4 shadow-sm text-center custom-card" +
                (selectedAddOns.analytics ? " border border-primary" : "")
              }
            >
              <div className="icon-circle">
                <BarChart />
              </div>
              <h5 className="fw-bold">Analytics</h5>
              <p className="text-muted">
                Page views, traffic, and basic visitor insights.
              </p>
              <p className="fw-bold mb-0">From £3 per month</p>
              {selectedAddOns.analytics && (
                <p className="small text-primary mt-2 mb-0">
                  Included in estimate
                </p>
              )}
            </div>
          </button>
        </div>

        <div className="col-md-4">
          <div className="p-4 rounded-4 shadow-sm text-center custom-card">
            <div className="icon-circle">
              <Files />
            </div>
            <h5 className="fw-bold">Extra pages</h5>
            <p className="text-muted">
              Add more pages as your business grows.
            </p>
            <p className="fw-bold mb-1">£{EXTRA_PAGE_PRICE} per page</p>
            <div className="d-flex justify-content-center align-items-center gap-3 mt-3">
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={removePage}
              >
                –
              </button>
              <span className="fw-bold">{extraPages}</span>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={addPage}
              >
                +
              </button>
            </div>
            {extraPages > 0 && (
              <p className="small text-primary mt-2 mb-0">
                {extraPages} page{extraPages > 1 ? "s" : ""} added to estimate
              </p>
            )}
          </div>
        </div>

        <div className="col-md-4">
          <button
            onClick={() => toggleAddOn("ecommerce")}
            className="w-100 btn p-0 border-0 bg-transparent"
          >
            <div
              className={
                "p-4 rounded-4 shadow-sm text-center custom-card" +
                (selectedAddOns.ecommerce ? " border border-primary" : "")
              }
            >
              <div className="icon-circle">
                <Shop />
              </div>
              <h5 className="fw-bold">Ecommerce</h5>
              <p className="text-muted">Product pages, cart, and checkout.</p>
              <p className="fw-bold mb-0">From £15 per month</p>
              {selectedAddOns.ecommerce && (
                <p className="small text-primary mt-2 mb-0">
                  Included in estimate
                </p>
              )}
            </div>
          </button>
        </div>

        <div className="col-md-4">
          <button
            onClick={() => toggleAddOn("pwa")}
            className="w-100 btn p-0 border-0 bg-transparent"
          >
            <div
              className={
                "p-4 rounded-4 shadow-sm text-center custom-card" +
                (selectedAddOns.pwa ? " border border-primary" : "")
              }
            >
              <div className="icon-circle">
                <Phone />
              </div>
              <h5 className="fw-bold">Progressive web app</h5>
              <p className="text-muted">
                Installable on phones with better offline support.
              </p>
              <p className="fw-bold mb-0">From £4 per month</p>
              {selectedAddOns.pwa && (
                <p className="small text-primary mt-2 mb-0">
                  Included in estimate
                </p>
              )}
            </div>
          </button>
        </div>

        <div className="col-md-4">
          <button
            onClick={() => toggleAddOn("database")}
            className="w-100 btn p-0 border-0 bg-transparent"
          >
            <div
              className={
                "p-4 rounded-4 shadow-sm text-center custom-card" +
                (selectedAddOns.database ? " border border-primary" : "")
              }
            >
              <div className="icon-circle">
                <Database />
              </div>
              <h5 className="fw-bold">Database system</h5>
              <p className="text-muted">
                Store clients, bookings, or other records.
              </p>
              <p className="fw-bold mb-0">From £12 per month</p>
              {selectedAddOns.database && (
                <p className="small text-primary mt-2 mb-0">
                  Included in estimate
                </p>
              )}
            </div>
          </button>
        </div>

        <div className="col-md-4">
          <button
            onClick={() => toggleAddOn("custom")}
            className="w-100 btn p-0 border-0 bg-transparent"
          >
            <div
              className={
                "p-4 rounded-4 shadow-sm text-center custom-card" +
                (selectedAddOns.custom ? " border border-primary" : "")
              }
            >
              <div className="icon-circle">
                <Gear />
              </div>
              <h5 className="fw-bold">Custom features</h5>
              <p className="text-muted">
                Integrations, dashboards, and extra functionality.
              </p>
              <p className="fw-bold mb-0">From £8 per month</p>
              {selectedAddOns.custom && (
                <p className="small text-primary mt-2 mb-0">
                  Included in estimate
                </p>
              )}
            </div>
          </button>
        </div>
      </div>

      <div className="text-center my-5">
        <Link to={"/concept"} className="btn brand-purple-outline">
          Talk About A Project
        </Link>
      </div>

      <div className="row justify-content-center mb-5">
        <div className="col-md-6">
          <div className="p-4 rounded-4 shadow-sm text-center custom-card-light">
            <h3 className="fw-bold mb-3">Website Build and Hosting</h3>
            <h2 className="fw-bold">£19.50 per month</h2>
            <p className="text-muted mt-3">
              A modern one page website built for your business and hosted for a
              low monthly price.
            </p>
            <ul className="list-unstyled mt-3">
              <li>Responsive design</li>
              <li>Fast page loading</li>
              <li>Hosting included</li>
              <li>Basic updates</li>
            </ul>
            <p className="text-muted mt-2">
              If you do not already own a domain name I will lease one for you
              and include the cost in your monthly payment.
            </p>
            <p>
              Fill in a short form describing the website you want and I will
              get in touch with a fair price.
            </p>
            <Link
              to={"/concept"}
              className="btn brand-purple-inline text-white w-100 mt-3"
            >
              Start form
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
